import { Injectable, OnDestroy } from '@angular/core';
import {
    CustomerMapToken,
    CustomerMapsService
} from '@flyfreely-portal-ui/flyfreely';
import { BehaviorSubject, Subject, Subscription, of, timer } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';

/**
 * Manages the customer map token lifecycle, including initial fetch, proactive refresh
 * before expiry, and cleanup on destroy.
 */
@Injectable()
export class CustomerMapTokenManager implements OnDestroy {
    /**
     * A sentinel value used when no real token is available (e.g. admin contexts).
     * It satisfies `!= null` so the map renders, but carries no credentials.
     */
    static readonly NO_TOKEN: CustomerMapToken = {
        accessToken: '',
        baseUrl: ''
    };

    /** How many milliseconds before expiry to trigger a proactive refresh. */
    private static readonly TOKEN_REFRESH_BUFFER_MS = 60_000;

    private mapTokenSource = new BehaviorSubject<CustomerMapToken>(
        CustomerMapTokenManager.NO_TOKEN
    );

    /**
     * The customer map token, used to authenticate tile/source requests.
     * Starts as NO_TOKEN (a non-null sentinel) until the real token is loaded.
     */
    mapToken$ = this.mapTokenSource.asObservable();

    /** The organisation ID for which the current map token was fetched. */
    private tokenOrganisationId: number | null = null;

    /** Subscription for the scheduled token-refresh timer. */
    private tokenRefreshSubscription: Subscription | null = null;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(private customerMapsService: CustomerMapsService) {}

    ngOnDestroy() {
        this.cancelTokenRefresh();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.mapTokenSource.complete();
    }

    /**
     * Fetches the map token for the given organisation and sets up proactive refresh.
     * Pass `null` to clear the token back to NO_TOKEN.
     */
    setupForOrganisation(organisationId: number | null) {
        this.cancelTokenRefresh();
        this.tokenOrganisationId = organisationId;

        if (organisationId == null || this.customerMapsService == null) {
            this.mapTokenSource.next(CustomerMapTokenManager.NO_TOKEN);
            return;
        }

        this.customerMapsService
            .findToken(organisationId)
            .pipe(
                take(1),
                takeUntil(this.ngUnsubscribe$),
                catchError(() => of(null))
            )
            .subscribe(token => {
                const resolved = token ?? CustomerMapTokenManager.NO_TOKEN;
                this.mapTokenSource.next(resolved);
                if (resolved.accessToken) {
                    this.scheduleTokenRefresh(resolved.accessToken);
                }
            });
    }

    // -------------------------------------------------------------------------
    // Private helpers
    // -------------------------------------------------------------------------

    /**
     * Decodes the `exp` claim from a JWT access token without verifying the signature.
     * Returns the expiry as a Unix timestamp in milliseconds, or `null` if the token
     * cannot be decoded or has no `exp` claim.
     */
    private getTokenExpiryMs(accessToken: string): number | null {
        try {
            const parts = accessToken.split('.');
            if (parts.length !== 3) {
                return null;
            }
            const payload = JSON.parse(
                atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))
            );
            if (typeof payload.exp !== 'number') {
                return null;
            }
            return payload.exp * 1000;
        } catch {
            return null;
        }
    }

    /** Cancels any previously scheduled token refresh. */
    private cancelTokenRefresh() {
        if (this.tokenRefreshSubscription) {
            this.tokenRefreshSubscription.unsubscribe();
            this.tokenRefreshSubscription = null;
        }
    }

    /**
     * Schedules a token refresh to fire `TOKEN_REFRESH_BUFFER_MS` before the
     * token expires. If the token has no decodable expiry the method is a no-op.
     */
    private scheduleTokenRefresh(accessToken: string) {
        this.cancelTokenRefresh();

        const expiryMs = this.getTokenExpiryMs(accessToken);
        if (expiryMs == null || this.tokenOrganisationId == null) {
            return;
        }

        const delayMs =
            expiryMs -
            Date.now() -
            CustomerMapTokenManager.TOKEN_REFRESH_BUFFER_MS;

        if (delayMs <= 0) {
            this.refreshToken(this.tokenOrganisationId);
            return;
        }

        this.tokenRefreshSubscription = timer(delayMs)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => this.refreshToken(this.tokenOrganisationId));
    }

    /**
     * Fetches a fresh map token for the given organisation and stores it.
     * Also reschedules the next refresh based on the new token's expiry.
     */
    private refreshToken(organisationId: number) {
        this.customerMapsService
            .findToken(organisationId)
            .pipe(
                take(1),
                takeUntil(this.ngUnsubscribe$),
                catchError(() => of(null))
            )
            .subscribe(token => {
                const resolved = token ?? CustomerMapTokenManager.NO_TOKEN;
                this.mapTokenSource.next(resolved);
                if (resolved.accessToken) {
                    this.scheduleTokenRefresh(resolved.accessToken);
                }
            });
    }
}
